import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { getSalesNoteLinesApi } from "api/salesNote";
import { SalesNote, SalesNoteLine } from "interface/salesNote";
import { ICON_NAMES } from "utils/constant";
import { GrideDateFormatter } from "ui/Date";
import { formatAmount } from "utils/formatters";
import { formatWeight } from "utils/formatters";

interface SalesNotesMobileListProps {
	items: SalesNote[];
	loading: boolean;
	onEdit: (item: SalesNote) => void;
	onOpenPdf: (item: SalesNote) => void;
	onToggleChecked: (item: SalesNote) => void;
	isChecked: (id: string) => boolean;
	canCheck: (status: number) => boolean;
	onLoadMore: () => void;
	onDelete: (item: SalesNote) => void;
}

const getStatusClassName = (status: number, signStatus?: number) => {
	if (status === 1) return "sales-note-list-card--status-red";
	if (status === 2) return "sales-note-list-card--status-yellow";
	if (status === 4 && signStatus === 0)
		return "sales-note-list-card--status-orange";
	return "sales-note-list-card--status-green";
};

const SalesNotesMobileList: FC<SalesNotesMobileListProps> = ({
	items,
	loading,
	onEdit,
	onOpenPdf,
	onToggleChecked,
	isChecked,
	canCheck,
	onLoadMore,
	onDelete,
}) => {
	const { t } = useTranslation();
	const [expandedNotes, setExpandedNotes] = useState<number[]>([]);
	const [lineMap, setLineMap] = useState<Record<number, SalesNoteLine[]>>({});
	const [lineLoadingMap, setLineLoadingMap] = useState<Record<number, boolean>>(
		{},
	);

	const empty = useMemo(
		() => !loading && items.length === 0,
		[items.length, loading],
	);

	const toggleLines = (item: SalesNote) => {
		if (expandedNotes.includes(item.noteNumber)) {
			setExpandedNotes((prev) => prev.filter((id) => id !== item.noteNumber));
			return;
		}

		setExpandedNotes((prev) => [...prev, item.noteNumber]);
		if (lineMap[item.noteNumber] || lineLoadingMap[item.noteNumber]) {
			return;
		}

		setLineLoadingMap((prev) => ({ ...prev, [item.noteNumber]: true }));

		getSalesNoteLinesApi({ noteNumber: item.noteNumber, source: item.source })
			.then((response) => {
				setLineMap((prev) => ({
					...prev,
					[item.noteNumber]: (response?.data || []) as SalesNoteLine[],
				}));
			})
			.finally(() => {
				setLineLoadingMap((prev) => ({ ...prev, [item.noteNumber]: false }));
			});
	};

	return (
		<div className="sales-note-list-mobile">
			{empty && (
				<div className="sales-note-list-mobile-empty">{t("NoRows")}</div>
			)}

			{items.map((item, itemIndex) => {
				const expanded = expandedNotes.includes(item.noteNumber);
				const lines = lineMap[item.noteNumber] || [];
				const linesLoading = lineLoadingMap[item.noteNumber] === true;
				const checkAllowed = canCheck(item.noteStatusId);
				const showEdit = item.source === 0;
				const showSignature = item.noteStatusId === 4;
				const itemKey = `${item.noteNumber}-${item.source ?? "nosource"}-${itemIndex}`;

				return (
					<div
						key={itemKey}
						className={`sales-note-list-card ${getStatusClassName(item.noteStatusId, item.signStatus)}`}
					>
						<div className="sales-note-list-card-header">
							<div className="sales-note-list-card-note">{item.noteNumber}</div>
							<div className="sales-note-list-card-vessel">
								{item.vesselName || "-"}
							</div>
							<div className="sales-note-list-card-date">
								<GrideDateFormatter
									date={item.landingTime}
									formatType="dd.MM.yyyy"
									noTd={true}
								/>
							</div>
						</div>

						<div className="sales-note-list-card-body">
							<div>
								<span>{t("VesselRegistrationId")}</span>
								<strong>{item.vesselRegistrationId || "-"}</strong>
							</div>
							<div>
								<span>{t("FishingGear")}</span>
								<strong>{item.fishingGear || "-"}</strong>
							</div>
							<div>
								<span>{t("Buyer")}</span>
								<strong>{item.buyerName || "-"}</strong>
							</div>
							<div>
								<span>{t("CatchArea")}</span>
								<strong>
									{item.catchField || item.catchArea || "-"}
									{item.catchAreaZone ? ` / ${item.catchAreaZone}` : ""}
								</strong>
							</div>
							<div>
								<span>{t("TotalWeight")}</span>
								<strong>{formatWeight(item.netLiveWeightKg)}</strong>
							</div>
							<div>
								<span>{t("Amount")}</span>
								<strong>{formatAmount(item.amount)}</strong>
							</div>
						</div>

						<div className="sales-note-list-card-actions">
							{(showEdit || showSignature) && (
								<button
									type="button"
									className="sales-note-list-icon-btn"
									onClick={() => onEdit(item)}
									title={showSignature ? t("Signature") : t("Edit")}
								>
									<img
										src={`/icon/${showSignature ? ICON_NAMES.Signature : ICON_NAMES.Edit}.svg`}
										alt={showSignature ? t("Signature") : t("Edit")}
									/>
								</button>
							)}
							{showEdit && (
								<button
									type="button"
									className="sales-note-list-icon-btn"
									onClick={() => onDelete(item)}
									title={t("Delete")}
								>
									<img
										src={`/icon/${ICON_NAMES.Delete}.svg`}
										alt={t("Delete")}
									/>
								</button>
							)}
							<button
								type="button"
								className="sales-note-list-icon-btn"
								onClick={() => onOpenPdf(item)}
								title={t("Download")}
							>
								<img
									src={`/icon/${ICON_NAMES.Download}.svg`}
									alt={t("Download")}
								/>
							</button>
							<button
								type="button"
								className="sales-note-list-icon-btn"
								onClick={() => onToggleChecked(item)}
								disabled={!checkAllowed}
								title={t("CatchCertificate")}
							>
								<img
									src={`/icon/${isChecked(String(item.noteNumber)) ? ICON_NAMES.Checkbox : ICON_NAMES.CheckboxBlack}.svg`}
									alt={t("CatchCertificate")}
								/>
							</button>
							<button
								type="button"
								className="sales-note-list-icon-btn"
								onClick={() => toggleLines(item)}
								title={t("Lines")}
							>
								<img
									src={`/icon/${expanded ? ICON_NAMES.ArrowUp : ICON_NAMES.ArrowDown}.svg`}
									alt={t("Lines")}
								/>
							</button>
						</div>

						{expanded && (
							<div className="sales-note-list-lines">
								{linesLoading && (
									<div className="sales-note-list-lines-loading">
										{t("Loading")}
									</div>
								)}
								{!linesLoading && lines.length === 0 && (
									<div className="sales-note-list-lines-empty">
										{t("NoRows")}
									</div>
								)}
								{!linesLoading &&
									lines.map((line, lineIndex) => (
										<div
											key={`${line.id ?? "noid"}-${line.lineNumber ?? "noline"}-${lineIndex}`}
											className="sales-note-list-line-row"
										>
											<div className="sales-note-list-line-number">
												#{line.lineNumber}
											</div>
											<div className="sales-note-list-line-product">
												{line.product || "-"}
											</div>
											<div className="sales-note-list-line-weight">
												{formatWeight(line.netLiveWeightKg)}
											</div>
										</div>
									))}
							</div>
						)}
					</div>
				);
			})}

			{loading && (
				<div className="sales-note-list-mobile-loading">{t("Loading")}</div>
			)}
		</div>
	);
};

export default SalesNotesMobileList;
